import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { fetchPaymentHistory, fetchServerDateTime } from 'store/thunks/adminPaymentThunks';
import { IAdminPayment } from 'types';
import { ESliceDataFetchingStatus } from 'types/common';

export interface IAdminPaymentState {
  status: ESliceDataFetchingStatus;
  errorMessage: string | null;
  serverDateTime: string | null;
  payments: IAdminPayment[];
  pagesQuantity: number;
  itemsPerPage: number;
  currentPage: number;
  sortBy: string | null;
  sortDir: string | null;
  search: string | null;
  confirmation: string | null;
  createdFromDate: string | null;
  createdToDate: string | null;
  updatedFromDate: string | null;
  updatedToDate: string | null;
}

const initialState: IAdminPaymentState = {
  status: ESliceDataFetchingStatus.initial,
  errorMessage: null,
  serverDateTime: null,
  payments: [],
  pagesQuantity: 1,
  itemsPerPage: 10,
  currentPage: 0,
  sortBy: null,
  sortDir: null,
  search: null,
  confirmation: null,
  createdFromDate: null,
  createdToDate: null,
  updatedFromDate: null,
  updatedToDate: null,
};

export const adminPaymentsSlice = createSlice({
  name: 'adminPayments',
  initialState,
  reducers: {
    paymentsListSetPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setItemsPerPaymentsListPage: (state, action: PayloadAction<number>) => {
      state.itemsPerPage = action.payload;
      state.currentPage = initialState.currentPage;
    },
    setSort: (
      state,
      action: PayloadAction<{
        sortBy: IAdminPaymentState['sortBy'];
        sortDir: IAdminPaymentState['sortDir'];
      }>,
    ) => {
      state.sortBy = action.payload.sortBy;
      state.sortDir = action.payload.sortDir;
    },
    setSearch: (state, action: PayloadAction<IAdminPaymentState['search']>) => {
      state.search = action.payload;
    },
    setConfirmationStatusFilter: (
      state,
      action: PayloadAction<IAdminPaymentState['confirmation']>,
    ) => {
      state.confirmation = action.payload;
    },
    setCreatedAtDateFilter: (
      state,
      action: PayloadAction<{
        fromDate: IAdminPaymentState['createdFromDate'];
        toDate: IAdminPaymentState['createdToDate'];
      }>,
    ) => {
      state.createdFromDate = action.payload.fromDate;
      state.createdToDate = action.payload.toDate;
    },
    setUpdatedAtDateFilter: (
      state,
      action: PayloadAction<{
        fromDate: IAdminPaymentState['updatedFromDate'];
        toDate: IAdminPaymentState['updatedToDate'];
      }>,
    ) => {
      state.updatedFromDate = action.payload.fromDate;
      state.updatedToDate = action.payload.toDate;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchPaymentHistory.fulfilled, (state, { payload }) => {
        state.status = ESliceDataFetchingStatus.success;
        state.payments = payload.content;
        state.pagesQuantity = payload.details.totalPages;
      })
      .addCase(fetchPaymentHistory.pending, (state) => {
        state.status = ESliceDataFetchingStatus.loading;
        state.errorMessage = null;
      })
      .addCase(fetchPaymentHistory.rejected, (state, action) => {
        state.status = ESliceDataFetchingStatus.error;
        state.errorMessage = action.error.message || 'unknown fetch payment history list error';
      })
      .addCase(fetchServerDateTime.fulfilled, (state, { payload }) => {
        state.status = ESliceDataFetchingStatus.success;
        state.serverDateTime = payload.time;
      })
      .addCase(fetchServerDateTime.pending, (state) => {
        state.status = ESliceDataFetchingStatus.loading;
        state.errorMessage = null;
      })
      .addCase(fetchServerDateTime.rejected, (state, action) => {
        state.status = ESliceDataFetchingStatus.error;
        state.errorMessage = action.error.message || 'unknown fetch server date error';
      }),
});

export const selectAdminPayments = (state: RootState) => state.adminPayments;

export const {
  paymentsListSetPage,
  setItemsPerPaymentsListPage,
  setSort,
  setSearch,
  setConfirmationStatusFilter,
  setCreatedAtDateFilter,
  setUpdatedAtDateFilter,
} = adminPaymentsSlice.actions;

export const adminPaymentsReducer = adminPaymentsSlice.reducer;
